/**
 * Wave 11 — Easter eggs.
 *
 * Hidden delights: the Konami code, typing "genie", and a triple-tap on the
 * logo. Discovered eggs persist so the collection feels like a real find.
 */

const STORAGE_KEY = "gb_easter_eggs_v1";

export type EggId = "konami" | "genie" | "midnight" | "confetti-storm";

export interface EggDefinition {
  id: EggId;
  name: string;
  hint: string;
}

export const EGGS: EggDefinition[] = [
  { id: "konami", name: "Retro Genie", hint: "An old-school cheat code still works here." },
  { id: "genie", name: "Say My Name", hint: "Type who you're talking to." },
  { id: "midnight", name: "Night Owl", hint: "Visit GenieBot after midnight." },
  { id: "confetti-storm", name: "Confetti Storm", hint: "Triple-tap the logo." },
];

const KONAMI = [
  "ArrowUp", "ArrowUp", "ArrowDown", "ArrowDown",
  "ArrowLeft", "ArrowRight", "ArrowLeft", "ArrowRight",
  "b", "a",
];

export function getFoundEggs(): EggId[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as EggId[]) : [];
  } catch {
    return [];
  }
}

export function markEggFound(id: EggId): boolean {
  const found = getFoundEggs();
  if (found.includes(id)) return false;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...found, id]));
  } catch {
    /* noop */
  }
  return true;
}

/**
 * Stateful matcher for keyboard-driven eggs. Feed it every keydown key.
 */
export function createEggMatcher(onEgg: (id: EggId) => void) {
  let konamiIndex = 0;
  let typed = "";

  return (key: string) => {
    // Konami
    const expected = KONAMI[konamiIndex];
    if (key.toLowerCase() === expected.toLowerCase()) {
      konamiIndex += 1;
      if (konamiIndex === KONAMI.length) {
        konamiIndex = 0;
        onEgg("konami");
      }
    } else {
      konamiIndex = key === KONAMI[0] ? 1 : 0;
    }

    // Typed word
    if (/^[a-z]$/i.test(key)) {
      typed = (typed + key.toLowerCase()).slice(-8);
      if (typed.endsWith("genie")) {
        typed = "";
        onEgg("genie");
      }
    } else {
      typed = "";
    }
  };
}

/** Burst of confetti using DOM nodes — no dependency, respects reduced motion. */
export function celebrate(count = 60): void {
  if (typeof document === "undefined") return;
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

  const layer = document.createElement("div");
  layer.setAttribute("aria-hidden", "true");
  layer.style.cssText =
    "position:fixed;inset:0;pointer-events:none;z-index:9999;overflow:hidden";
  document.body.appendChild(layer);

  const colors = ["hsl(var(--primary))", "hsl(var(--accent))", "hsl(var(--secondary))"];
  for (let i = 0; i < count; i += 1) {
    const piece = document.createElement("span");
    const size = 6 + Math.random() * 8;
    piece.style.cssText = [
      "position:absolute",
      `left:${Math.random() * 100}%`,
      "top:-20px",
      `width:${size}px`,
      `height:${size * 0.6}px`,
      `background:${colors[i % colors.length]}`,
      `opacity:${0.6 + Math.random() * 0.4}`,
      "border-radius:2px",
      `transform:rotate(${Math.random() * 360}deg)`,
      `animation:gb-egg-fall ${1.6 + Math.random() * 1.4}s linear forwards`,
      `animation-delay:${Math.random() * 0.4}s`,
    ].join(";");
    layer.appendChild(piece);
  }

  window.setTimeout(() => layer.remove(), 3600);
}
